import type { FunnelStageDatum, FunnelTransitionDatum } from "@bi/shared";

export interface FlowNode extends FunnelStageDatum {
  x: number;
  y: number;
  height: number;
  terminal: boolean;
  guestY: number;
  signedHeight: number;
  guestHeight: number;
}

export interface FlowEdge extends FunnelTransitionDatum {
  path: string;
  skipped: boolean;
  guestPath: string;
}

export interface FlowDrop {
  key: string;
  path: string;
  guestPath: string;
}

/** Layout observed paths without inventing the rungs a project bypassed.
 * Terminal outcomes partition the final main node, at project or budget grain.
 * All positive edges must be drawable and every node must conserve its flow.
 */
export function layoutFunnelFlow(
  stages: readonly FunnelStageDatum[],
  transitions: readonly FunnelTransitionDatum[],
  outcomeKeys: readonly string[],
) {
  const terminalKeys = new Set(outcomeKeys);
  const main = stages.filter((s) => !terminalKeys.has(s.key));
  const byKey = new Map(stages.map((s) => [s.key, s]));
  if (byKey.size !== stages.length || terminalKeys.size !== outcomeKeys.length) {
    throw new Error("The funnel contains duplicate stages or outcomes.");
  }
  if (!main.length || outcomeKeys.some((key) => !byKey.has(key))) {
    throw new Error("The funnel is missing a main stage or a declared outcome.");
  }
  const last = main[main.length - 1]!;
  const index = new Map(main.map((s, i) => [s.key, i]));
  const seen = new Set<string>();
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, number>();
  for (const edge of transitions) {
    const key = `${edge.fromKey}->${edge.toKey}`;
    const from = index.get(edge.fromKey);
    const to = index.get(edge.toKey);
    const terminal = terminalKeys.has(edge.toKey);
    if (
      seen.has(key) ||
      from === undefined ||
      (!terminal && (to === undefined || to <= from)) ||
      (terminal && edge.fromKey !== last.key) ||
      !Number.isFinite(edge.count) ||
      edge.count < 0 ||
      !Number.isFinite(edge.guestCount) ||
      edge.guestCount < 0 ||
      edge.guestCount > edge.count
    ) {
      throw new Error(`Invalid observed funnel path: ${key}.`);
    }
    seen.add(key);
    incoming.set(edge.toKey, (incoming.get(edge.toKey) ?? 0) + edge.count);
    outgoing.set(edge.fromKey, (outgoing.get(edge.fromKey) ?? 0) + edge.count);
  }
  const equal = (a: number, b: number) => Math.abs(a - b) <= Math.max(0.000001, Math.abs(b) * 1e-9);
  for (const [i, stage] of stages.entries()) {
    if (
      ![stage.reached, stage.stalled, stage.guestReached, stage.guestStalled].every(
        (n) => Number.isFinite(n) && n >= 0,
      ) ||
      stage.guestReached > stage.reached ||
      stage.guestStalled > stage.stalled ||
      (i !== 0 && !equal(incoming.get(stage.key) ?? 0, stage.reached)) ||
      !equal((outgoing.get(stage.key) ?? 0) + stage.stalled, stage.reached)
    ) {
      throw new Error(`The observed paths do not add up at "${stage.label}".`);
    }
    const guestIn = transitions
      .filter((e) => e.toKey === stage.key)
      .reduce((n, e) => n + e.guestCount, 0);
    const guestOut = transitions
      .filter((e) => e.fromKey === stage.key)
      .reduce((n, e) => n + e.guestCount, 0);
    if (
      (i !== 0 && !equal(guestIn, stage.guestReached)) ||
      !equal(guestOut + stage.guestStalled, stage.guestReached)
    ) {
      throw new Error(`The guest paths do not add up at "${stage.label}".`);
    }
  }
  const outcomes = outcomeKeys.map((key) => byKey.get(key)!);
  if (
    !equal(
      outcomes.reduce((sum, s) => sum + s.reached, 0),
      last.reached,
    )
  ) {
    throw new Error("The order outcomes do not partition the final stage.");
  }

  const width = 1480;
  const columnWidth = (width - 160) / Math.max(main.length - 1, 1);
  const scale = 180 / Math.max(main[0]!.reached, 1);
  const nodes: FlowNode[] = [...main, ...outcomes].map((s, i) => {
    const signedHeight = (s.reached - s.guestReached) * scale;
    const guestHeight = s.guestReached * scale;
    const gap = signedHeight > 0 && guestHeight > 0 ? 4 : 0;
    const y = 140 - (signedHeight + guestHeight + gap) / 2;
    return {
      ...s,
      x: 74 + Math.min(i, main.length - 1) * columnWidth,
      y,
      height: s.reached * scale,
      signedHeight,
      guestHeight,
      guestY: y + signedHeight + gap,
      terminal: terminalKeys.has(s.key),
    };
  });
  const positions = new Map(nodes.map((n) => [n.key, n]));
  const fromOffset = new Map<string, number>();
  const toOffset = new Map<string, number>();
  // Stable order gives every band its own slice at both ends, including bypasses.
  const ordered = [...transitions].sort(
    (a, b) =>
      index.get(a.fromKey)! - index.get(b.fromKey)! ||
      stages.findIndex((s) => s.key === a.toKey) - stages.findIndex((s) => s.key === b.toKey),
  );
  const band = (
    x1: number,
    x2: number,
    y1: number,
    y2: number,
    thickness: number,
    skipped: boolean,
  ) => {
    if (thickness <= 0) return "";
    const mid = (x1 + x2) / 2;
    return skipped
      ? `M ${x1} ${y1} C ${x1 + 35} 260, ${x2 - 35} 260, ${x2} ${y2} L ${x2} ${y2 + thickness} C ${x2 - 35} ${260 + thickness}, ${x1 + 35} ${260 + thickness}, ${x1} ${y1 + thickness} Z`
      : `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2} L ${x2} ${y2 + thickness} C ${mid} ${y2 + thickness}, ${mid} ${y1 + thickness}, ${x1} ${y1 + thickness} Z`;
  };
  const edges: FlowEdge[] = ordered.map((edge) => {
    const from = positions.get(edge.fromKey)!;
    const to = positions.get(edge.toKey)!;
    const x1 = from.x + 12;
    const x2 = to.x;
    const skipped = !to.terminal && index.get(to.key)! - index.get(from.key)! > 1;
    const segment = (guest: boolean) => {
      const thickness = (guest ? edge.guestCount : edge.count - edge.guestCount) * scale;
      const fk = `${from.key}:${guest}`,
        tk = `${to.key}:${guest}`;
      const y1 = (guest ? from.guestY : from.y) + (fromOffset.get(fk) ?? 0);
      const y2 = (guest ? to.guestY : to.y) + (toOffset.get(tk) ?? 0);
      fromOffset.set(fk, (fromOffset.get(fk) ?? 0) + thickness);
      toOffset.set(tk, (toOffset.get(tk) ?? 0) + thickness);
      // Outcomes are rendered in a responsive, four-branch row below the graph.
      return to.terminal ? "" : band(x1, x2, y1, y2, thickness, skipped);
    };
    return { ...edge, skipped, path: segment(false), guestPath: segment(true) };
  });
  const drops: FlowDrop[] = nodes
    .filter((n) => !n.terminal && n.stalled > 0)
    .map((node) => {
      const segment = (guest: boolean) => {
        const thickness = (guest ? node.guestStalled : node.stalled - node.guestStalled) * scale;
        if (thickness <= 0) return "";
        const y = (guest ? node.guestY : node.y) + (fromOffset.get(`${node.key}:${guest}`) ?? 0);
        const x = node.x + 12,
          end = x + 28;
        return `M ${x} ${y} C ${end} ${y + 30}, ${x} 330, ${end} 350 L ${end} ${350 + Math.min(thickness, 3)} C ${x - 8} 330, ${end} ${y + thickness + 30}, ${x} ${y + thickness} Z`;
      };
      return { key: node.key, path: segment(false), guestPath: segment(true) };
    });
  return {
    nodes,
    edges,
    drops,
    width,
    height: 398,
  };
}
